import React, { ReactElement, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography } from '@mui/material';

import { getComparator, Order, RowsPerPageOptions, RowsPerPageValues } from '..';
import EnhancedTableHead, { EnhancedTableHeadCell } from '../EnhancedTableHead';

interface PaginationProps {
	page?: number;
	setPage?: (newPage: number) => void;
	rowsPerPage?: number;
	setRowsPerPage?: (newRows: RowsPerPageOptions) => void;
}

interface EnhancedTableProps<T> extends PaginationProps {
	defaultOrder?: Order;
	defaultOrderBy: string;
	headers: EnhancedTableHeadCell[];
	rowData: T[];
	rowFunction: (row: T, index: number) => React.ReactNode;
	tableTitle: string;
	maxHeight?: string;
}

function EnhancedTable<T>(props: React.PropsWithChildren<EnhancedTableProps<T>>): ReactElement {
	const {
		defaultOrder = 'asc',
		defaultOrderBy,
		headers,
		rowData,
		rowFunction,
		page = 0,
		setPage,
		rowsPerPage = 5,
		setRowsPerPage,
		tableTitle,
		maxHeight,
	} = props;

	const [order, setOrder] = useState<Order>(defaultOrder);
	const [orderBy, setOrderBy] = useState<string>(defaultOrderBy);

	const numRows = Object.keys(rowData).length;

	const handleRequestSort = (_: React.MouseEvent, property: string) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	return (
		<Box>
			<TableContainer data-testid={`${tableTitle}-table`} sx={{ maxHeight: maxHeight || undefined, overflow: 'scroll' }}>
				<Table aria-labelledby="tableTitle" size="medium" aria-label={`${tableTitle} Table`} stickyHeader>
					<EnhancedTableHead cells={headers} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
					<TableBody>
						{numRows > 0 ? (
							rowData
								.slice()
								.sort(getComparator(order, orderBy))
								.slice(setPage ? page * rowsPerPage : 0, setPage ? page * rowsPerPage + rowsPerPage : undefined)
								.map((row, idx) => rowFunction(row, page * rowsPerPage + idx))
						) : (
							<TableRow>
								<TableCell colSpan={6}>
									<Typography>No entries found</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{setPage && setRowsPerPage && (
				<TablePagination
					rowsPerPageOptions={RowsPerPageValues}
					component={'div'}
					count={Object.keys(rowData).length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={(_, newPage) => setPage(newPage)}
					onRowsPerPageChange={(e) => {
						setRowsPerPage(parseInt(e.target.value));
						setPage(0);
					}}
					labelRowsPerPage={<Typography component={'span'}>Rows Per Page</Typography>}
					showFirstButton
					showLastButton
					data-testid={`${tableTitle}-pagination`}
				/>
			)}
		</Box>
	);
}

export default EnhancedTable;
